import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { TaskType } from '@/shared/domain/taskTypes';
import { useCommercialModels } from '@/shared/hooks/useCommercialModels';
import ProviderWarningBanner from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/ProviderWarningBanner';
import { analyzeProviderGate } from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/providerGate';
import ClassificationFlow from '@/features/datasets/components/DataCollection/GenerateDataWithAI/flows/ClassificationFlow';
import MultiLabelFlow from '@/features/datasets/components/DataCollection/GenerateDataWithAI/flows/MultiLabelFlow';
import NerFlow from '@/features/datasets/components/DataCollection/GenerateDataWithAI/flows/NerFlow';
import SummarizationFlow from '@/features/datasets/components/DataCollection/GenerateDataWithAI/flows/SummarizationFlow';

const flowForUsecase = (usecase) => {
    const taskType = usecase?.task_type;
    if (taskType === TaskType.NER) return NerFlow;
    if (taskType === TaskType.summarization || taskType === TaskType.contentGeneration) {
        return SummarizationFlow;
    }
    if (taskType === TaskType.classification && usecase?.is_multilabel) return MultiLabelFlow;
    return ClassificationFlow;
};

/**
 * "Generate data with AI" entry point: routes to the task-type-specific flow
 * (classification / multi-label / entity extraction / summarization &
 * content generation) and owns the provider gating warning (no providers
 * configured, or only classification-only TypeSafe AI configured).
 */
const GenerateDataWithAIModal = ({ visible, onCancel, onGenerate, usecase }) => {
    const {
        availableProviders,
        isLoading: isLoadingProviders,
        refresh: refreshCommercialModels,
    } = useCommercialModels();
    const { hasNoProviders, typeSafeOnly } = analyzeProviderGate(
        availableProviders,
        isLoadingProviders
    );

    // The catalog is cached in localStorage and refreshed on credential saves
    // in this tab only; re-fetch on open so a tab left open across a
    // credentials change never offers a provider without keys.
    useEffect(() => {
        if (visible) refreshCommercialModels?.();
    }, [visible, refreshCommercialModels]);

    const warningBanner =
        hasNoProviders || typeSafeOnly ? (
            <ProviderWarningBanner featureName="Generate Data with AI" typeSafeOnly={typeSafeOnly} />
        ) : null;

    const Flow = flowForUsecase(usecase);
    return (
        <Flow
            open={visible}
            onClose={onCancel}
            onGenerate={onGenerate}
            usecase={usecase}
            warningBanner={warningBanner}
            disabled={hasNoProviders || typeSafeOnly}
        />
    );
};

GenerateDataWithAIModal.propTypes = {
    visible: PropTypes.bool.isRequired,
    onCancel: PropTypes.func.isRequired,
    onGenerate: PropTypes.func,
    usecase: PropTypes.object,
};

export default GenerateDataWithAIModal;
